import _ from "lodash";
import React, { useState, useEffect } from "react";
import { Segment, Button, Form, Header, Icon } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { initialize, Field, reduxForm, FieldArray } from "redux-form";
import { createRetailer } from "../firebase";
import { ApiRequest } from "../firebase/server";
import { fetchRetailers, openModal, closeModal } from "../actions";
import { TextInput, SelectInput } from "../templates/utils/form-fields";

let stores = [];
let edit = false;

const TemplateList = ({ fields, openTemplate, service }) => {
  return (
    <>
      <Form.Field>
        <Header as="h3" className="float-left">
          Templates
        </Header>
        <Icon
          size="large"
          color="teal"
          onClick={() => fields.push({ service })}
          link
          name="plus"
          className="pushdown"
        />
      </Form.Field>
      {fields.map((template, index) => (
        <Form.Group key={index} className="clearboth">
          <Form.Field>
            <label>Display name</label>
            <Field
              type="text"
              name={`${template}.displayName`}
              component={TextInput}
              value={template.displayName}
            />
          </Form.Field>
          <Form.Field>
            <label>Template name</label>
            <Field
              type="text"
              name={`${template}.name`}
              component={TextInput}
              value={template.name}
            />
          </Form.Field>
          {service === "kevel" && (
            <Form.Field>
              <label>Description</label>
              <Field
                type="text"
                name={`${template}.description`}
                component={TextInput}
                value={template.description}
              />
            </Form.Field>
          )}
          <Form.Field>
            <label>Template id</label>
            <Field
              type="text"
              name={`${template}.id`}
              component={TextInput}
              value={template.id}
              disabled={service === "kevel"}
            />
          </Form.Field>
          {service === "gam" && (
            <Form.Field>
              <label>Native id</label>
              <Field
                type="text"
                name={`${template}.styleId`}
                component={TextInput}
                value={template.styleId}
              />
            </Form.Field>
          )}
          <Form.Field>
            <Field
              name={`${template}.type`}
              label="Type"
              type="text"
              component={SelectInput}
              options={[
                { text: "bespoke", value: "bespoke", key: "bespoke" },
                { text: "custom", value: "custom", key: "custom" },
                { text: "native", value: "native", key: "native" },
                { text: "standard", value: "standard", key: "standard" },
                { text: "hybrid", value: "hybrid", key: "hybrid" },
              ]}
              multiple={false}
            />
          </Form.Field>
          <Form.Field>
            <Field
              component={TextInput}
              type="hidden"
              name={`${template}.service`}
              value={service}
            />
          </Form.Field>
          {fields.get(index).type && fields.get(index).type !== "bespoke" && (
            <Form.Field>
              <label>&nbsp;</label>
              <Icon
                name="edit"
                onClick={() => openTemplate(fields, index)}
                size="large"
                color="blue"
                link
              />
            </Form.Field>
          )}
          <Form.Field>
            <label>&nbsp;</label>
            <Icon
              onClick={() => fields.remove(index)}
              link
              size="large"
              color="red"
              name="trash"
            />
          </Form.Field>
        </Form.Group>
      ))}
    </>
  );
};

let Templates = ({
  activeItem,
  handleSubmit,
  pristine,
  invalid,
  submitting,
}) => {
  const dispatch = useDispatch();
  const retailers = useSelector((state) => state.retailers);
  const [retailer, setRetailer] = useState({});
  const [update, setUpdate] = useState(false);

  const clearForm = () => {
    setUpdate(false);
    setRetailer({ displayName: "", name: "", templates: [] });
  };

  const handleSubmitForm = (values) => {
    dispatch(
      openModal("ProgressModal", {
        title: values.displayName,
        name: `Updating ${values.displayName}`,
        progress: 50,
      })
    );
    createRetailer(values)
      .then((data) => {
        const retailerArray = update
          ? _.map(retailers, (item) => {
              if (item.displayName === data.displayName) item = data;
              return item;
            })
          : [...retailers, data];
        dispatch(fetchRetailers(retailerArray));
        clearForm();
        dispatch(
          openModal("ProgressModal", {
            title: values.displayName,
            name: `${data.displayName} has been updated`,
            progress: 100,
          })
        );
        window.setTimeout(() => {
          dispatch(closeModal());
        }, 2000);
      })
      .catch((error) => console.log(error));
  };

  const openTemplate = (fields, id) => {
    //console.log("fields", fields);
    const t = fields.get(id);
    if (t) {
      dispatch(
        openModal("LoadingModal", {
          centered: false,
          title: "Fetching template",
        })
      );
      if (t.type === "native" || t.type === "custom" || t.type === "hybrid") {
        ApiRequest({
          templateId: t.id,
          retailer: retailer.name,
          service: retailer.service,
          call: "getTemplate",
        })
          .then((data) => {
            dispatch(
              openModal("TemplateModal", {
                centered: false,
                retailer,
                data,
                index: id,
                service: retailer.service,
              })
            );
          })
          .catch((error) => {
            dispatch(
              openModal("ErrorModal", {
                centered: false,
                type: "negative",
                error: {
                  name: `Fetching template`,
                  error: `Sorry, we're unable to fetch this template from the server, please check your settings`,
                },
              })
            );
          });
      }
      if (t.type === "standard") {
        dispatch(
          openModal("TemplateModal", {
            centered: false,
            retailer,
            index: id,
            service: retailer.service,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (activeItem.name) {
      setRetailer({ ...activeItem });
      setUpdate(true);
    } else {
      setRetailer({});
      setUpdate(false);
    }
  }, [activeItem]);

  useEffect(() => {
    stores = retailers;
  }, [retailers]);

  useEffect(() => {
    edit = update;
  }, [update]);

  useEffect(() => {
    dispatch(initialize("retailerForm", { ...retailer }));
  }, [dispatch, retailer]);

  return (
    <Segment>
      {!activeItem.name && <Header as="h3">Create new retailer</Header>}
      <Form onSubmit={handleSubmit(handleSubmitForm)}>
        <Form.Group>
          <Field
            label="Display name"
            type="text"
            name="displayName"
            component={TextInput}
            value={retailer.displayName}
            disabled={update}
          />
          <Form.Field>
            <Field
              label="Code name"
              type="text"
              name="name"
              component={TextInput}
              value={retailer.name}
              disabled={update}
            />
          </Form.Field>
          <Form.Field>
            <Field
              label="logo"
              type="text"
              name="logo"
              component={TextInput}
              value={retailer.logo}
            />
          </Form.Field>
        </Form.Group>
        <FieldArray
          name="templates"
          component={TemplateList}
          openTemplate={openTemplate}
          service={activeItem.service}
        />
        <Button
          className="clearboth"
          positive
          size="tiny"
          type="submit"
          disabled={submitting || pristine || invalid}
        >
          Submit
        </Button>
      </Form>
    </Segment>
  );
};

const validate = (values) => {
  const errors = {};
  const templateArrayErrors = [];

  if (!values.displayName) {
    errors.displayName = "Please enter a retailer name";
  }
  if (!values.name) {
    errors.name = "Please enter a code name";
  }

  if (values.templates) {
    values.templates.forEach((template, index) => {
      const templateErrors = {};
      if (!template || !template.displayName) {
        templateErrors.displayName = "Please enter a display name";
        templateArrayErrors[index] = templateErrors;
      }
      if (!template || !template.name) {
        templateErrors.name = "Please enter a template name";
        templateArrayErrors[index] = templateErrors;
      }
      //if (!template || !template.id) {
      //  templateErrors.id = "Please enter a template id";
      //  templateArrayErrors[index] = templateErrors;
      //}
    });
  }

  if (stores && !edit) {
    if (values.displayName || values.name) {
      stores.forEach((store) => {
        if (store.displayName === values.displayName)
          errors.displayName = "This display name already exists";
        if (store.name === values.name)
          errors.name = "This code name already exists";
      });
    }
  }

  if (templateArrayErrors.length) {
    errors.templates = templateArrayErrors;
  }

  return errors;
};

Templates = reduxForm({
  validate,
  form: "retailerForm",
})(Templates);

//const selector = formValueSelector("retailerForm");
//Templates = connect((state) => {
//  const templateValues = selector(state, "templates");
//  return {
//    templateValues,
//  };
//})(Templates);

export default Templates;
